import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Lock } from "lucide-react";
import { Logo } from "@/components/Shared";
import { usePageTitle } from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AdminLogin = () => {
  const navigate = useNavigate();
  usePageTitle("Back office");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("orbit_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="relative flex min-h-screen items-center justify-center bg-[#0A0F1D] px-5">
      <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
      <div className="pointer-events-none absolute -top-24 left-1/3 h-96 w-96 rounded-full bg-blue-600/15 blur-3xl" />
      <div className="relative w-full max-w-md">
        <Link
          to="/"
          data-testid="admin-back-home-link"
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Retour au site
        </Link>
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="rounded-2xl border border-white/10 bg-white/[0.04] p-9 backdrop-blur-xl"
        >
          <Logo dark className="h-9" />
          <h1 className="mt-8 font-display text-2xl font-bold text-white">Back office</h1>
          <p className="mt-2 text-sm text-slate-400">Connectez-vous pour gérer le contenu du site.</p>

          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                placeholder="admin@orbit.fr"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Mot de passe
              </label>
              <input
                id="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <p data-testid="admin-login-error" className="mt-4 rounded-lg bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
          >
            <Lock className="h-4 w-4" />
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </div>
    </main>
  );
};

export default AdminLogin;
